// Hook: loads the possible-repeat flags (db-210) for one request.
//
// Calls request_repeat_matches() once when an INTERNAL user opens a request (pass
// enabled = false for DFV Service users and anyone else — they never see the flag).
// Best-effort: a failed lookup leaves `byItem` empty and `failed` true; it never throws
// and never blocks the screen. `reloadKey` re-runs the lookup (e.g. after a confirm).
//
// See src/services/repeatCheck.ts (display helpers) and
// supabase/migrations/00000000000049_request_repeat_matches.sql (the rules).

import React from "react";
import { getRepeatMatches } from "../data/repeatMatches";
import { listInternalNotesByRequest } from "../data/requestInternalNotes";
import {
  groupRepeatMatches,
  latestRepeatReview,
  type RepeatMatchLike,
  type RepeatSummary,
  type RepeatsByItem,
  repeatSummary,
} from "../services/repeatCheck";

export interface RepeatCheckState {
  byItem: RepeatsByItem;
  summary: RepeatSummary;
  /** The latest Repeat check note (internal note, kind "repeat_check"), or null. */
  review: { author: string; text: string; date: string } | null;
  loaded: boolean;
  failed: boolean;
}

const EMPTY: RepeatsByItem = {};

export function useRepeatCheck(
  requestId: number | string | null | undefined,
  opts: { enabled: boolean; months?: number | null; types?: string[] | null; reloadKey?: unknown }
): RepeatCheckState {
  const { enabled, months, reloadKey } = opts;
  // A stable key for the types list so a new array with the same contents doesn't re-query.
  const typesKey = opts.types == null ? null : JSON.stringify(opts.types);
  const [state, setState] = React.useState<RepeatCheckState>({
    byItem: EMPTY,
    summary: repeatSummary(EMPTY),
    review: null,
    loaded: false,
    failed: false,
  });

  // Deps: typesKey stands in for opts.types; reloadKey is a deliberate re-run trigger.
  React.useEffect(
    function () {
      let alive = true;
      if (!enabled || !requestId) {
        setState({
          byItem: EMPTY,
          summary: repeatSummary(EMPTY),
          review: null,
          loaded: true,
          failed: false,
        });
        return;
      }
      const types: string[] | null = typesKey == null ? null : JSON.parse(typesKey);
      Promise.all([
        getRepeatMatches(requestId, { months, types }),
        listInternalNotesByRequest(requestId),
      ]).then(function ([res, notes]) {
        if (!alive) return;
        const byItem = groupRepeatMatches(res.rows as RepeatMatchLike[]);
        setState({
          byItem,
          summary: repeatSummary(byItem),
          review: latestRepeatReview(notes),
          loaded: true,
          failed: !!res.error,
        });
      });
      return function () {
        alive = false;
      };
    },
    [requestId, enabled, months, typesKey, reloadKey]
  );

  return state;
}

/**
 * A FRESH repeat lookup for Confirm & Send (24 Oct 2026 fix). The screen's flags are loaded
 * once when the request opens, so a twin submitted while it was open was missed and no
 * Repeat check note was asked for (2026-03110). Confirm & Send calls this first.
 *
 * Returns the grouped matches, or null when the lookup failed or took longer than
 * `timeoutMs` — the caller then falls back to the on-screen flags. Never throws, so it can
 * never stop a confirm.
 */
export async function loadFreshRepeats(
  requestId: number | string | null | undefined,
  settings: { months?: number | null; types?: string[] | null },
  timeoutMs = 5000
): Promise<RepeatsByItem | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>(function (resolve) {
      timer = setTimeout(function () {
        resolve(null);
      }, timeoutMs);
    });
    const res = await Promise.race([getRepeatMatches(requestId, settings), timeout]);
    if (!res || res.error) return null;
    return groupRepeatMatches(res.rows as RepeatMatchLike[]);
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
